import React, { useState, useEffect, ReactElement } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  SelectChangeEvent,
  InputAdornment,
  Chip,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Watch as WatchIcon,
  RemoveCircle as RemoveCircleIcon,
  History as HistoryIcon,
  RestartAlt as RestartAltIcon,
  MonitorHeart as MonitorHeartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import MemberSafetyBaselineDialog from '../member/MemberSafetyBaselineDialog';
import MemberReportDialog from '../../pages/reports/MemberReportDialog';
import { memberApi, Member, MemberCreate, MemberUpdate } from '../../services/memberApi';
import { wearableApi, Wearable, WearableMapping } from '../../services/wearableApi';
import {
  isMemberUsageWearable,
  wearableUsagePurposeLabel,
} from '../../constants/wearableUsagePurpose';
import { BLOOD_TYPE_OPTIONS, isSupportedBloodType } from '../../constants/bloodTypes';
import { ApiResponse } from '../../types';
import axios from 'axios';
import { API_ORIGIN } from '../../config';

interface MemberManagementProps {
  poolId: string;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  onRefreshData?: () => void;
}

const MemberManagement: React.FC<MemberManagementProps> = ({ poolId, showSnackbar, onRefreshData }): ReactElement => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [formData, setFormData] = useState<Partial<Member>>({
    name: '',
    birth_date: '',
    gender: 'M',
    blood_type: '',
    phone: '',
    email: '',
    guardian_name: '',
    guardian_phone: '',
    address: '',
    leave_date: '',
    created_at: '',
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openWearableDialog, setOpenWearableDialog] = useState<boolean>(false);
  const [availableWearables, setAvailableWearables] = useState<Wearable[]>([]);
  const [selectedWearableId, setSelectedWearableId] = useState<number | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [loadingWearables, setLoadingWearables] = useState<boolean>(false);
  const [totalMembers, setTotalMembers] = useState<number>(0);
  const [openWearableHistoryDialog, setOpenWearableHistoryDialog] = useState<boolean>(false);
  const [wearableHistory, setWearableHistory] = useState<WearableMapping[]>([]);
  const [loadingWearableHistory, setLoadingWearableHistory] = useState<boolean>(false);
  const [selectedMemberForHistory, setSelectedMemberForHistory] = useState<Member | null>(null);
  const [wearableSearchTerm, setWearableSearchTerm] = useState<string>('');
  const [openResetDialog, setOpenResetDialog] = useState<boolean>(false);
  const [resettingWearables, setResettingWearables] = useState<boolean>(false);
  const [openDeleteHistoryDialog, setOpenDeleteHistoryDialog] = useState<boolean>(false);
  const [deletingHistory, setDeletingHistory] = useState<boolean>(false);
  const [baselineDialog, setBaselineDialog] = useState<{
    open: boolean;
    memberId: number;
    memberName: string;
  }>({ open: false, memberId: 0, memberName: '' });
  const [reportDialog, setReportDialog] = useState<{
    open: boolean;
    memberId: number | null;
    memberName: string;
  }>({ open: false, memberId: null, memberName: '' });

  // API 기본 URL
  const API_URL = API_ORIGIN;

  // 회원 목록 가져오기
  const fetchMembers = async () => {
    if (!poolId) {
      setError('시설 ID가 제공되지 않음');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('회원 목록 조회 시작: 시설 ID', poolId);
      const response = await memberApi.getMembers(String(poolId), 1, 10, searchTerm);
      if (response.success && response.data) {
        // 회원 이름순으로 정렬
        const sortedMembers = [...response.data].sort((a, b) => 
          a.name.localeCompare(b.name, 'ko')
        );
        
        setMembers(sortedMembers);
        setTotalMembers(sortedMembers.length);
        console.log(`회원 ${sortedMembers.length}명 로드 성공:`, sortedMembers);
      } else {
        console.error('회원 목록 조회 실패:', response.message);
        setError(response.message || '회원 목록을 불러오기 실패');
      }
    } catch (error) {
      console.error('회원 목록 조회 중 오류:', error);
      setError('회원 목록을 불러오기 실패');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 회원 목록 가져오기
  useEffect(() => {
    if (poolId) {
      fetchMembers();
    }
  }, [poolId]);

  // 페이지 변경 핸들러
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // 페이지당 행 수 변경 핸들러
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 다이얼로그 열기 - 회원 추가
  const handleOpenAddDialog = () => {
    setDialogMode('add');
    const now = new Date().toISOString().slice(0, 16);
    setFormData({
      name: '',
      birth_date: '',
      gender: 'M',
      blood_type: '',
      phone: '',
      email: '',
      guardian_name: '',
      guardian_phone: '',
      address: '',
      leave_date: '',
      created_at: now,
    });
    setOpenDialog(true);
  };

  // 다이얼로그 열기 - 회원 수정
  const handleOpenEditDialog = (member: Member) => {
    setDialogMode('edit');
    setSelectedMember(member);
    setFormData({
      name: member.name,
      birth_date: member.birth_date,
      gender: member.gender,
      blood_type: member.blood_type,
      phone: member.phone,
      email: member.email,
      guardian_name: member.guardian_name || '',
      guardian_phone: member.guardian_phone || '',
      address: member.address,
      leave_date: member.leave_date || '',
      created_at: member.created_at || '',
    });
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // 폼 데이터 변경 핸들러 - 일반 텍스트 필드
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // 폼 데이터 변경 핸들러 - 셀렉트 필드
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // 날짜를 HTML datetime-local 입력에 사용할 수 있는 형식으로 변환
  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // YYYY-MM-DDThh:mm 형식으로 변환 (datetime-local 입력에 필요한 형식)
      return date.toISOString().slice(0, 16);
    } catch (e) {
      return '';
    }
  };
  
  // 날짜를 사용자 친화적인 형식으로 표시
  const formatDateForDisplay = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      // 한국 날짜 형식으로 표시 
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return '-';
    }
  };

  // 회원 저장 (추가/수정)
  const handleSaveMember = async () => {
    try {
      if (!formData.name || !formData.birth_date || !formData.gender || 
          !formData.blood_type || !formData.phone || !formData.address) {
        showSnackbar('필수 항목을 모두 입력', 'error');
        return;
      }

      const backendData: MemberCreate | MemberUpdate = {
        name: formData.name,
        birth_date: formData.birth_date,
        gender: formData.gender,
        blood_type: formData.blood_type,
        phone: formData.phone,
        email: formData.email || undefined,
        guardian_name: formData.guardian_name || undefined,
        guardian_phone: formData.guardian_phone || undefined,
        address: formData.address,
        created_at: formData.created_at || undefined,
        leave_date: formData.leave_date || undefined
      };

      let response: ApiResponse<Member>;
      
      if (dialogMode === 'add') {
        response = await memberApi.createMember(Number(poolId), backendData as MemberCreate);
      } else {
        response = await memberApi.updateMember(Number(poolId), selectedMember!.id, backendData as MemberUpdate);
      }

      if (response.success) {
        showSnackbar(dialogMode === 'add' ? '회원이 등록 완료' : '회원 정보가 수정됨', 'success');
        handleCloseDialog();
        fetchMembers();
        if (onRefreshData) {
          onRefreshData();
        }
      } else {
        showSnackbar(response.message || '회원 정보 저장에 실패', 'error');
      }
    } catch (error) {
      console.error('회원 정보 저장 중 오류:', error);
      showSnackbar('회원 정보 저장에 실패', 'error');
    }
  };

  // 회원 삭제
  const handleDeleteMember = async (memberId: number) => {
    if (!window.confirm('정말로 이 회원을 삭제 확인')) {
      return;
    }

    try {
      const response = await memberApi.deleteMember(Number(poolId), memberId);
      
      if (response.success) {
        await fetchMembers();
        showSnackbar('회원이 삭제 완료', 'success');
      } else {
        showSnackbar(response.message || '회원 삭제에 실패', 'error');
      }
    } catch (err) {
      console.error('회원 삭제 중 오류:', err);
      showSnackbar(`회원 삭제에 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`, 'error');
    }
  };

  // 검색 기능
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // 검색 결과 필터링
  const filteredMembers = members.filter(member => 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone.includes(searchTerm) ||
    (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // 현재 페이지에 표시할 회원 목록
  const paginatedMembers = filteredMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // 웨어러블 할당 다이얼로그 열기
  const handleOpenWearableDialog = async (memberId: number) => {
    setSelectedMemberId(memberId);
    setLoadingWearables(true);
    setOpenWearableDialog(true);
    setWearableSearchTerm(''); // 검색어 초기화
    setAvailableWearables([]); // 이전 데이터 초기화
    
    try {
      console.log('웨어러블 할당 대화상자 열기: 회원 ID', memberId);
      
      // 1. 모든 웨어러블 가져오기
      const allWearablesResponse = await wearableApi.getAllWearables(String(poolId));
      if (!allWearablesResponse.success || !allWearablesResponse.data) {
        showSnackbar('웨어러블 목록을 가져오기 실패', 'error');
        setLoadingWearables(false);
        return;
      }
      
      console.log('전체 웨어러블 목록:', allWearablesResponse.data.map(w => ({ id: w.id, code: w.code, member_id: w.current_member_id })));
      
      // 2. 현재 할당된 웨어러블 ID 목록 가져오기
      // 모든 회원의 현재 웨어러블 ID 목록 수집
      const assignedWearableIds = members
        .filter(member => member.current_wearable_id) // current_wearable_id가 있는 회원만 필터링
        .map(member => {
          // string이나 null 타입일 수 있으므로 숫자로 변환
          const wearableId = member.current_wearable_id ? parseInt(member.current_wearable_id) : null;
          return wearableId;
        })
        .filter(id => id !== null); // null 제거
      
      console.log('현재 할당된 웨어러블 ID 목록:', assignedWearableIds);
      
      // 3. 사용 가능한 웨어러블 필터링 및 코드 순서로 정렬
      const availableWearables = allWearablesResponse.data
        .filter((wearable) => isMemberUsageWearable(wearable.usage_purpose))
        .filter(wearable => !assignedWearableIds.includes(wearable.id))
        .sort((a, b) => {
          // 코드가 없는 경우를 처리
          const codeA = a.code || '';
          const codeB = b.code || '';
          return codeA.localeCompare(codeB);
        });
      
      console.log('사용 가능한 웨어러블 목록:', availableWearables.map(w => ({ id: w.id, code: w.code })));
      
      setAvailableWearables(availableWearables);
    } catch (err) {
      console.error('웨어러블 목록 조회 중 오류:', err);
      showSnackbar('웨어러블 목록을 가져오기 실패', 'error');
    } finally {
      setLoadingWearables(false);
    }
  };
  
  // 웨어러블 할당 다이얼로그 닫기
  const handleCloseWearableDialog = () => {
    setOpenWearableDialog(false);
    setSelectedWearableId(null);
  };
  
  // 웨어러블 코드 검색 핸들러
  const handleWearableSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWearableSearchTerm(e.target.value);
  };

  // 웨어러블 검색 필터링
  const filteredWearables = availableWearables.filter(wearable =>
    (wearable.code?.toLowerCase() || '').includes(wearableSearchTerm.toLowerCase()) ||
    (wearable.name?.toLowerCase() || '').includes(wearableSearchTerm.toLowerCase())
  );

  // 웨어러블 할당 저장
  const handleSaveWearable = async () => {
    if (!selectedMemberId || !selectedWearableId) {
      showSnackbar('회원과 웨어러블을 선택', 'error');
      return;
    }

    setLoadingWearables(true);
    try {
      const response = await memberApi.assignWearableToMember(Number(poolId), selectedMemberId, selectedWearableId);
      
      if (response.success) {
        showSnackbar('웨어러블이 할당됨', 'success');
        setOpenWearableDialog(false);
        await fetchMembers();
      } else {
        showSnackbar(response.message || '웨어러블 할당에 실패', 'error');
      }
    } catch (err) {
      console.error('웨어러블 할당 중 오류:', err);
      showSnackbar('웨어러블 할당 중 오류 발생', 'error');
    } finally {
      setLoadingWearables(false);
    }
  };
  
  // 웨어러블 반납
  const handleReturnWearable = async (memberId: number) => {
    try {
      console.log('[반납 DEBUG] 회원 ID', memberId, '의 웨어러블 반납 시작');
      const response = await wearableApi.returnWearable(Number(poolId), memberId);
      if (response.success) {
        showSnackbar('웨어러블이 반납됨', 'success');
        await fetchMembers();
      } else {
        showSnackbar(response.message || '웨어러블 반납에 실패', 'error');
      }
    } catch (error) {
      console.error('웨어러블 반납 중 오류:', error);
      showSnackbar('웨어러블 반납 중 오류 발생', 'error');
    }
  };

  // 웨어러블 이력 갱신 함수
  const refreshWearableHistory = async (memberId: number) => {
    try {
      console.log(`[이력갱신] 회원 ID ${memberId}의 웨어러블 사용 이력 갱신 시작`);
      setLoadingWearableHistory(true);
      
      // 최신 이력 가져오기
      const response = await wearableApi.getMemberWearableHistory(String(poolId), memberId);
      console.log(`[이력갱신] 웨어러블 사용 이력 응답:`, response);
      
      if (response.success && response.data) {
        setWearableHistory(response.data);
        console.log(`[이력갱신] 사용 이력 ${response.data.length}건 로드 완료`);
      } else {
        console.error(`[이력갱신] 사용 이력 로드 실패:`, response.message);
      }
    } catch (err) {
      console.error(`[이력갱신] 사용 이력 갱신 중 오류:`, err);
    } finally {
      setLoadingWearableHistory(false);
    }
  };

  // 웨어러블 이력 다이얼로그 열기
  const handleOpenWearableHistoryDialog = async (member: Member) => {
    setSelectedMemberForHistory(member);
    setLoadingWearableHistory(true);
    setOpenWearableHistoryDialog(true);
    
    try {
      const response = await wearableApi.getMemberWearableHistory(String(poolId), member.id);
      
      if (response.success && response.data) {
        setWearableHistory(response.data);
      } else {
        showSnackbar(response.message || '웨어러블 사용 이력을 가져오기 실패', 'error');
      }
    } catch (err) {
      console.error('웨어러블 사용 이력 조회 중 오류:', err);
      showSnackbar('웨어러블 사용 이력을 가져오기 실패', 'error');
    } finally {
      setLoadingWearableHistory(false);
    }
  };
  
  // 웨어러블 이력 다이얼로그 닫기
  const handleCloseWearableHistoryDialog = () => {
    setOpenWearableHistoryDialog(false);
    setSelectedMemberForHistory(null);
    setWearableHistory([]);
  };

  // 웨어러블 기록 초기화 대화상자 열기
  const handleOpenResetDialog = () => {
    setOpenResetDialog(true);
  };
  
  // 웨어러블 기록 초기화 대화상자 닫기
  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
  };
  
  // 웨어러블 기록 초기화 실행
  const handleResetWearableMappings = async () => {
    if (!poolId) {
      showSnackbar('시설 ID가 없음', 'error');
      return;
    }
    
    setResettingWearables(true);
    try {
      console.log('[초기화 DEBUG] 웨어러블 기록 초기화 시작');
      const response = await wearableApi.resetAllWearableMappings(Number(poolId));
      console.log('[초기화 DEBUG] 응답 데이터:', response);
      
      if (response && response.success) {
        showSnackbar(response.message || '모든 웨어러블 기록이 초기화됨', 'success');
        await fetchMembers(); // 회원 목록 새로고침
      } else {
        showSnackbar(response?.message || '웨어러블 기록 초기화에 실패', 'error');
      }
    } catch (error) {
      console.error('웨어러블 기록 초기화 중 오류:', error);
      showSnackbar('웨어러블 기록 초기화에 실패', 'error');
    } finally {
      setResettingWearables(false);
      setOpenResetDialog(false);
    }
  };

  // 개인 사용이력 삭제 대화상자 열기
  const handleOpenDeleteHistoryDialog = () => {
    if (!selectedMemberForHistory) return;
    setOpenDeleteHistoryDialog(true);
  };
  
  // 개인 사용이력 삭제 대화상자 닫기
  const handleCloseDeleteHistoryDialog = () => {
    setOpenDeleteHistoryDialog(false);
  };
  
  // 개인 사용이력 삭제 실행
  const handleDeleteMemberWearableHistory = async () => {
    if (!poolId || !selectedMemberForHistory) {
      showSnackbar('필요한 정보 없음', 'error');
      return;
    }
    
    setDeletingHistory(true);
    try {
      const response = await memberApi.clearMemberWearableHistory(Number(poolId), selectedMemberForHistory.id);
      
      if (response.success) {
        showSnackbar(`${selectedMemberForHistory.name}님의 모든 웨어러블 사용 이력이 삭제 완료`, 'success');
        // 사용 이력 다이얼로그 닫기
        handleCloseWearableHistoryDialog();
        // 회원 목록 새로고침
        await fetchMembers();
      } else {
        showSnackbar(response.message || '웨어러블 사용 이력 삭제에 실패', 'error');
      }
    } catch (err) {
      console.error('웨어러블 사용 이력 삭제 중 오류:', err);
      showSnackbar('웨어러블 사용 이력 삭제 중 오류 발생', 'error');
    } finally {
      setDeletingHistory(false);
      setOpenDeleteHistoryDialog(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          회원 관리
        </Typography>
        <Box>
          <IconButton onClick={fetchMembers} title="새로고침">
            <RefreshIcon />
          </IconButton>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleOpenAddDialog}
            sx={{ ml: 1 }}
          >
            회원 추가
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RestartAltIcon />}
            onClick={handleOpenResetDialog}
            sx={{ ml: 1 }}
          >
            웨어러블 기록 초기화
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="이름, 전화번호, 이메일로 검색"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <SearchIcon color="action" sx={{ mr: 1 }} />
              ),
            }}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>이름</TableCell>
              <TableCell>생년월일</TableCell>
              <TableCell>성별</TableCell>
              <TableCell>혈액형</TableCell>
              <TableCell>전화번호</TableCell>
              <TableCell>가입일자</TableCell>
              <TableCell>탈퇴일자</TableCell>
              <TableCell>웨어러블 코드</TableCell>
              <TableCell>작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMembers.length > 0 ? (
              paginatedMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.birth_date}</TableCell>
                  <TableCell>{member.gender === 'M' ? '남성' : '여성'}</TableCell>
                  <TableCell>{member.blood_type}</TableCell>
                  <TableCell>{member.phone}</TableCell>
                  <TableCell>{formatDateForDisplay(member.created_at)}</TableCell>
                  <TableCell>{formatDateForDisplay(member.leave_date)}</TableCell>
                  <TableCell>
                    {member.current_wearable_id ? (
                      <Chip
                        icon={<WatchIcon />}
                        label={member.current_wearable_code || '알 수 없음'}
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<RemoveCircleIcon />}
                        label="미할당"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenEditDialog(member)}
                      title="수정"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteMember(member.id)}
                      title="삭제"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenWearableDialog(member.id)}
                      disabled={!!member.current_wearable_id}
                      size="small"
                      title="웨어러블 기기 할당"
                    >
                      <WatchIcon />
                    </IconButton>
                    {member.current_wearable_id && (
                      <Tooltip title="웨어러블 기기 반납">
                        <IconButton
                          onClick={() => handleReturnWearable(member.id)}
                          size="small"
                        >
                          <RemoveCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton 
                      onClick={() => handleOpenWearableHistoryDialog(member)}
                      size="small"
                      title="웨어러블 할당 이력"
                    >
                      <HistoryIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="개인 안전 기준선"
                      onClick={() =>
                        setBaselineDialog({
                          open: true,
                          memberId: member.id,
                          memberName: member.name,
                        })
                      }
                    >
                      <MonitorHeartIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="회원 리포트"
                      onClick={() =>
                        setReportDialog({
                          open: true,
                          memberId: member.id,
                          memberName: member.name,
                        })
                      }
                    >
                      <AssessmentIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {searchTerm ? '검색 결과 없음' : '등록된 회원 없음'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredMembers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="페이지당 행 수:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </TableContainer>

      {/* 회원 추가/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? '회원 추가' : '회원 정보 수정'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
              필수 정보
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="이름"
                  name="name"
                  value={formData.name}
                  onChange={handleTextFieldChange}
                  required
                  error={!formData.name}
                  helperText={!formData.name ? "이름을 입력" : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="생년월일"
                  name="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={handleTextFieldChange}
                  required
                  error={!formData.birth_date}
                  helperText={!formData.birth_date ? "생년월일을 입력" : ""}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!formData.gender}>
                  <InputLabel>성별</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleSelectChange}
                    label="성별"
                  >
                    <MenuItem value="M">남성</MenuItem>
                    <MenuItem value="F">여성</MenuItem>
                  </Select>
                  {!formData.gender && <FormHelperText>성별 선택</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!formData.blood_type}>
                  <InputLabel>혈액형</InputLabel>
                  <Select
                    name="blood_type"
                    value={formData.blood_type}
                    onChange={handleSelectChange}
                    label="혈액형"
                  >
                    {formData.blood_type && !isSupportedBloodType(formData.blood_type) && (
                      <MenuItem value={formData.blood_type} disabled>
                        {formData.blood_type}형 (Rh 미등록 — 다시 선택 필요)
                      </MenuItem>
                    )}
                    {BLOOD_TYPE_OPTIONS.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.blood_type && <FormHelperText>혈액형 선택</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="전화번호"
                  name="phone"
                  value={formData.phone}
                  onChange={handleTextFieldChange}
                  required
                  error={!formData.phone}
                  helperText={!formData.phone ? "전화번호를 입력" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="주소"
                  name="address"
                  value={formData.address}
                  onChange={handleTextFieldChange}
                  required
                  error={!formData.address}
                  helperText={!formData.address ? "주소를 입력" : ""}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
              추가 정보
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="이메일"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleTextFieldChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="보호자 이름"
                  name="guardian_name"
                  value={formData.guardian_name}
                  onChange={handleTextFieldChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="보호자 전화번호"
                  name="guardian_phone"
                  value={formData.guardian_phone}
                  onChange={handleTextFieldChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="탈퇴일자"
                  name="leave_date"
                  type="date"
                  value={formData.leave_date}
                  onChange={handleTextFieldChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button 
            onClick={handleSaveMember} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.birth_date || !formData.gender || 
                     !formData.blood_type || !formData.phone || !formData.address}
          >
            {dialogMode === 'add' ? '추가' : '수정'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 웨어러블 할당 다이얼로그 */}
      <Dialog open={openWearableDialog} onClose={handleCloseWearableDialog} maxWidth="md">
        <DialogTitle>웨어러블 기기 할당</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 400 }}>
            {loadingWearables ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {availableWearables.length > 0 ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      회원에게 할당할 웨어러블 기기를 선택
                    </Typography>
                    
                    {/* 웨어러블 코드 검색 필드 */}
                    <TextField
                      fullWidth
                      label="웨어러블 코드 검색"
                      variant="outlined"
                      size="small"
                      value={wearableSearchTerm}
                      onChange={handleWearableSearch}
                      sx={{ mb: 2, mt: 1 }}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon color="action" sx={{ mr: 1 }} />
                        ),
                      }}
                    />
                    
                    {/* 웨어러블 그리드 목록 */}
                    <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                      <Grid container spacing={1}>
                        {filteredWearables.map(wearable => (
                          <Grid item xs={12} key={wearable.id}>
                            <Paper 
                              sx={{ 
                                p: 1, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                bgcolor: selectedWearableId === wearable.id ? 'primary.light' : 'background.paper',
                                '&:hover': { bgcolor: selectedWearableId === wearable.id ? 'primary.light' : 'action.hover' }
                              }}
                              onClick={() => setSelectedWearableId(wearable.id)}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <WatchIcon sx={{ mr: 1, color: selectedWearableId === wearable.id ? 'primary.contrastText' : 'primary.main' }} />
                                <Typography sx={{ fontWeight: selectedWearableId === wearable.id ? 'bold' : 'normal', color: selectedWearableId === wearable.id ? 'primary.contrastText' : 'text.primary' }}>
                                  {wearable.code ? `[${wearable.code}] ` : ''}{wearable.name}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                  size="small"
                                  label={wearableUsagePurposeLabel(wearable.usage_purpose)}
                                  color="info"
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  label={`상태: ${wearable.status}`}
                                  color={wearable.status === 'ACTIVE' || wearable.status === 'active' ? 'success' : 'default'}
                                />
                                <Chip 
                                  size="small" 
                                  label={`활성화: ${wearable.is_active ? 'True' : 'False'}`}
                                  color={wearable.is_active ? 'primary' : 'default'}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                    
                    {filteredWearables.length === 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        검색 조건에 맞는 웨어러블 없음
                      </Alert>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      <strong>참고:</strong> 회원에게 이미 할당된 웨어러블이 있는 경우, 기존 웨어러블은 자동으로 반납 처리됨.
                    </Typography>
                  </>
                ) : (
                  <Alert severity="info">
                    현재 사용 가능한 웨어러블 기기 없음. 새로운 웨어러블을 등록하거나
                    다른 회원이 사용 중인 웨어러블을 반납해야
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWearableDialog}>취소</Button>
          <Button 
            onClick={handleSaveWearable} 
            variant="contained" 
            color="primary"
            disabled={!selectedWearableId || loadingWearables || filteredWearables.length === 0}
          >
            할당
          </Button>
        </DialogActions>
      </Dialog>

      {/* 웨어러블 사용 이력 다이얼로그 */}
      <Dialog 
        open={openWearableHistoryDialog} 
        onClose={handleCloseWearableHistoryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMemberForHistory?.name}님의 웨어러블 사용 이력
        </DialogTitle>
        <DialogContent>
          {loadingWearableHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {wearableHistory.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>매핑 ID</TableCell>
                        <TableCell>웨어러블 ID</TableCell>
                        <TableCell>할당 시간</TableCell>
                        <TableCell>반납 시간</TableCell>
                        <TableCell>상태</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {wearableHistory.map((mapping) => (
                        <TableRow key={mapping.id}>
                          <TableCell>{mapping.id}</TableCell>
                          <TableCell>{mapping.wearable_id}</TableCell>
                          <TableCell>{formatDateForDisplay(mapping.assigned_at)}</TableCell>
                          <TableCell>{formatDateForDisplay(mapping.returned_at)}</TableCell>
                          <TableCell>
                            {mapping.returned_at ? (
                              <Chip size="small" label="반납됨" />
                            ) : (
                              <Chip size="small" color="success" label="사용 중" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ my: 2 }}>
                  웨어러블 사용 이력 없음
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWearableHistoryDialog}>닫기</Button>
          {wearableHistory.length > 0 && (
            <Button 
              onClick={handleOpenDeleteHistoryDialog} 
              color="error"
              startIcon={<DeleteIcon />}
            >
              사용이력 삭제
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 웨어러블 기록 초기화 확인 대화상자 */}
      <Dialog
        open={openResetDialog}
        onClose={handleCloseResetDialog}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          웨어러블 기록 초기화
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <Typography variant="body1" gutterBottom>
              모든 웨어러블 기기의 할당/반납 기록을 초기화 확인
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              주의: 이 작업은 되돌릴 수 없으며, 모든 웨어러블 기록이 영구적으로 삭제됨.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              현재 회원들에게 할당된 웨어러블 기기는 모두 반납 처리되며, 모든 웨어러블 기록이 초기화됨.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} color="primary">
            취소
          </Button>
          <Button 
            onClick={handleResetWearableMappings} 
            color="error" 
            variant="contained"
            disabled={resettingWearables}
            startIcon={resettingWearables ? <CircularProgress size={20} color="inherit" /> : <RestartAltIcon />}
          >
            {resettingWearables ? '초기화 중...' : '초기화 진행'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 개인 사용이력 삭제 확인 대화상자 */}
      <Dialog
        open={openDeleteHistoryDialog}
        onClose={handleCloseDeleteHistoryDialog}
        aria-labelledby="delete-history-dialog-title"
        aria-describedby="delete-history-dialog-description"
      >
        <DialogTitle id="delete-history-dialog-title">
          웨어러블 사용 이력 삭제
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <Typography variant="body1" gutterBottom>
              {selectedMemberForHistory?.name} 웨어러블 사용 이력 삭제 확인
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              주의: 이 작업은 되돌릴 수 없으며, 모든 웨어러블 사용 이력이 영구적으로 삭제됨.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              삭제된 웨어러블 사용 이력은 복구 불가
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteHistoryDialog} color="primary">
            취소
          </Button>
          <Button 
            onClick={handleDeleteMemberWearableHistory} 
            color="error" 
            variant="contained"
            disabled={deletingHistory}
            startIcon={deletingHistory ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deletingHistory ? '삭제 중...' : '삭제 진행'}
          </Button>
        </DialogActions>
      </Dialog>

      <MemberSafetyBaselineDialog
        open={baselineDialog.open}
        poolId={Number(poolId)}
        memberId={baselineDialog.memberId}
        memberName={baselineDialog.memberName}
        onClose={() => setBaselineDialog({ open: false, memberId: 0, memberName: '' })}
      />
      <MemberReportDialog
        open={reportDialog.open}
        memberId={reportDialog.memberId}
        memberName={reportDialog.memberName}
        onClose={() => setReportDialog({ open: false, memberId: null, memberName: '' })}
      />
    </Box>
  );
};

export default MemberManagement; 